/**
 * Export Agg System Summary to CSV
 */

import { COLUMN_MODES, GALLONS_PER_ACRE_FOOT } from "../constants";
import { downloadCSV } from "./downloadCSV";

/**
 * Export agg system summary data to CSV
 * @param {import("../types").AggSystemSummary} summary - Agg system summary data
 * @param {string} columnMode - Column display mode
 * @param {string} mode - Drilldown mode ("year-wells" or "wells-year")
 */
export const exportAggSystemSummary = (summary, columnMode, mode) => {
  const isYearWells = mode === "year-wells";
  const level1Label = isYearWells ? "Year" : "Well";
  const level2Label = isYearWells ? "Well" : "Year";

  const headers =
    columnMode === COLUMN_MODES.compact
      ? [
          "Agg System",
          level1Label,
          level2Label,
          "Hist. Permit (ac-ft)",
          "Oper. Permit (ac-ft)",
          "Total Permit (ac-ft)",
          "YTD (gal)",
          "Remaining (gal)",
          "YTD (ac-ft)",
          "Remaining (ac-ft)",
          "% Permit",
        ]
      : [
          "Agg System",
          level1Label,
          level2Label,
          "Hist. Permit (ac-ft)",
          "Oper. Permit (ac-ft)",
          "Total Permit (ac-ft)",
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
          "YTD (gal)",
          "Remaining (gal)",
          "YTD (ac-ft)",
          "Remaining (ac-ft)",
          "% Permit",
        ];

  const rows = [headers];

  const buildRow = (aggName, level1Val, level2Val, totals, pctPermit) => {
    const remainingGal =
      totals.h_permit_value_gal + totals.o_permit_value_gal - totals.ytd_gal;
    const remainingAf = remainingGal / GALLONS_PER_ACRE_FOOT;

    const row = [
      aggName,
      level1Val,
      level2Val,
      totals.h_permit_af,
      totals.o_permit_af,
      totals.total_permit_af,
    ];

    if (columnMode === COLUMN_MODES.full) {
      row.push(
        totals.jan,
        totals.feb,
        totals.mar,
        totals.apr,
        totals.may,
        totals.jun,
        totals.jul,
        totals.aug,
        totals.sep,
        totals.oct,
        totals.nov,
        totals.dec
      );
    }

    row.push(
      totals.ytd_gal,
      remainingGal,
      totals.ytd_af,
      remainingAf,
      pctPermit
    );

    return row;
  };

  summary.aggSystems.forEach((agg) => {
    // Agg System total row
    rows.push(
      buildRow(agg.agg_system_name, "", "", agg.totals, agg.pct_permit)
    );

    // Children (Years or Wells depending on mode)
    if (agg.children && agg.children.length > 0) {
      agg.children.forEach((child) => {
        if (isYearWells) {
          // child is a year group with wells
          rows.push(
            buildRow(
              agg.agg_system_name,
              child.year,
              "",
              child.totals,
              child.pct_permit
            )
          );

          // Wells under this year
          if (child.wells && child.wells.length > 0) {
            child.wells.forEach((well) => {
              rows.push(
                buildRow(
                  agg.agg_system_name,
                  child.year,
                  well.cuwcd_well_number || well.well_name,
                  well.totals,
                  well.pct_permit
                )
              );
            });
          }
        } else {
          // child is a well group with years
          rows.push(
            buildRow(
              agg.agg_system_name,
              child.cuwcd_well_number || child.well_name,
              "",
              child.totals,
              child.pct_permit
            )
          );

          // Years under this well
          if (child.years && child.years.length > 0) {
            child.years.forEach((yr) => {
              rows.push(
                buildRow(
                  agg.agg_system_name,
                  child.cuwcd_well_number || child.well_name,
                  yr.year,
                  yr.totals,
                  yr.pct_permit
                )
              );
            });
          }
        }
      });
    }
  });

  // Grand Totals row
  rows.push(
    buildRow(
      "GRAND TOTAL",
      "",
      "",
      summary.grandTotals,
      summary.grandTotals.pct_permit
    )
  );

  downloadCSV(rows, "agg-system-summary.csv");
};
